import { computeSlipPercent, computeTheoreticalSpeedKn } from './calculations';
import type { NoonReport, NoonReportCondition } from './types';

function toInputString(value: unknown): string {
  if (value == null || value === '') return '';
  return String(value);
}

function toIsoDate(value: unknown): string {
  if (!value) return '';
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function toIsoDateTimeLocal(value: unknown): string {
  if (!value) return '';
  const d = new Date(String(value));
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toNumber(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function toOptionalId(value: unknown): number | null | undefined {
  if (value === undefined) return undefined;
  if (value === null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function speedDisplayValues(pitchMm: unknown, avgRpm: unknown, avgStw: unknown) {
  const theoretical = computeTheoreticalSpeedKn(toNumber(pitchMm), toNumber(avgRpm));
  const slip = computeSlipPercent(theoretical, toNumber(avgStw));
  return {
    _theoreticalSpeedDisplay: theoretical != null ? theoretical.toFixed(3) : '',
    _slipPercentDisplay: slip != null ? slip.toFixed(2) : '',
  };
}

export function getInitialFormData(): Partial<NoonReport> & Record<string, unknown> {
  return {
    vesselId: undefined,
    reportDate: new Date().toISOString().slice(0, 10),
    reportTime: '12:00',
    gmtOffset: '+0',
    condition: 'LADEN' as NoonReportCondition,
    activities: [],
    latitude: '',
    longitude: '',
    eta: '',
    etd: '',
    nextPort: '',
    distanceCovered: undefined,
    distanceToGo: undefined,
    cargoType: '',
    cargoOnBoardMt: undefined,
    draftFwd: undefined,
    draftMid: undefined,
    draftAft: undefined,
    presentSpeed: undefined,
    avgSpeed24h: undefined,
    avgRpm: undefined,
    steamingHours: undefined,
    pitchMm: undefined,
    avgSog: undefined,
    avgStw: undefined,
    seaStateId: undefined,
    seaCurrentDirectionId: undefined,
    seaCurrentForce: '',
    windFromId: undefined,
    windForce: '',
    swellDirectionId: undefined,
    swellForce: '',
    weatherNotes: '',
    notes: '',
    robFoMt: undefined,
    robMgoMdoMt: undefined,
    robSystemOilL: undefined,
    robCylinderOilL: undefined,
    robDgSystemOilL: undefined,
    robHydrOilL: undefined,
    robFwM3: undefined,
    robSludgesM3: undefined,
    robBilgesM3: undefined,
    consMeFoMt: undefined,
    consDgFoMt: undefined,
    consBlrFoMt: undefined,
    consIggFoMt: undefined,
    consMeMgoMdoMt: undefined,
    consDgMgoMdoMt: undefined,
    consBlrMgoMdoMt: undefined,
    consIggMgoMdoMt: undefined,
    consIncineratorMgoMdoMt: undefined,
    consSystemOilL: undefined,
    consCylinderOilL: undefined,
    consDgSystemOilL: undefined,
    consHydrOilL: undefined,
    consFwM3: undefined,
    rhMeTotal: undefined,
    rhMeDaily: undefined,
    rhAux1Total: undefined,
    rhAux1Daily: undefined,
    rhAux2Total: undefined,
    rhAux2Daily: undefined,
    rhAux3Total: undefined,
    rhAux3Daily: undefined,
    rhEmergencyAuxTotal: undefined,
    rhEmergencyAuxDaily: undefined,
    rhBoilerTotal: undefined,
    rhBoilerDaily: undefined,
    rhIggTotal: undefined,
    rhIggDaily: undefined,
    rhIncineratorTotal: undefined,
    rhIncineratorDaily: undefined,
    _theoreticalSpeedDisplay: '',
    _slipPercentDisplay: '',
  };
}

export function mapItemToFormData(item: NoonReport): Partial<NoonReport> & Record<string, unknown> {
  const speedDisplays = speedDisplayValues(item.pitchMm, item.avgRpm, item.avgStw);
  return {
    vesselId: item.vesselId != null ? Number(item.vesselId) : undefined,
    vessel: item.vessel ?? undefined,
    reportDate: toIsoDate(item.reportDate),
    reportTime: toInputString(item.reportTime),
    gmtOffset: toInputString(item.gmtOffset) || '+0',
    condition: (toInputString(item.condition) || 'LADEN') as NoonReportCondition,
    activities: Array.isArray(item.activities) ? [...item.activities] : [],
    latitude: toInputString(item.latitude),
    longitude: toInputString(item.longitude),
    eta: toIsoDateTimeLocal(item.eta),
    etd: toIsoDateTimeLocal(item.etd),
    nextPort: toInputString(item.nextPort),
    distanceCovered: toNumber(item.distanceCovered),
    distanceToGo: toNumber(item.distanceToGo),
    cargoType: toInputString(item.cargoType),
    cargoOnBoardMt: toNumber(item.cargoOnBoardMt),
    draftFwd: toNumber(item.draftFwd),
    draftMid: toNumber(item.draftMid),
    draftAft: toNumber(item.draftAft),
    presentSpeed: toNumber(item.presentSpeed),
    avgSpeed24h: toNumber(item.avgSpeed24h),
    avgRpm: toNumber(item.avgRpm),
    steamingHours: toNumber(item.steamingHours),
    pitchMm: toNumber(item.pitchMm),
    avgSog: toNumber(item.avgSog),
    avgStw: toNumber(item.avgStw),
    seaStateId: item.seaStateId ?? item.seaState?.id ?? undefined,
    seaCurrentDirectionId: item.seaCurrentDirectionId ?? item.seaCurrentDirection?.id ?? undefined,
    seaCurrentForce: toInputString(item.seaCurrentForce),
    windFromId: item.windFromId ?? item.windFrom?.id ?? undefined,
    windForce: toInputString(item.windForce),
    swellDirectionId: item.swellDirectionId ?? item.swellDirection?.id ?? undefined,
    swellForce: toInputString(item.swellForce),
    weatherNotes: toInputString(item.weatherNotes),
    notes: toInputString(item.notes),
    robFoMt: toNumber(item.robFoMt),
    robMgoMdoMt: toNumber(item.robMgoMdoMt),
    robSystemOilL: toNumber(item.robSystemOilL),
    robCylinderOilL: toNumber(item.robCylinderOilL),
    robDgSystemOilL: toNumber(item.robDgSystemOilL),
    robHydrOilL: toNumber(item.robHydrOilL),
    robFwM3: toNumber(item.robFwM3),
    robSludgesM3: toNumber(item.robSludgesM3),
    robBilgesM3: toNumber(item.robBilgesM3),
    consMeFoMt: toNumber(item.consMeFoMt),
    consDgFoMt: toNumber(item.consDgFoMt),
    consBlrFoMt: toNumber(item.consBlrFoMt),
    consIggFoMt: toNumber(item.consIggFoMt),
    consMeMgoMdoMt: toNumber(item.consMeMgoMdoMt),
    consDgMgoMdoMt: toNumber(item.consDgMgoMdoMt),
    consBlrMgoMdoMt: toNumber(item.consBlrMgoMdoMt),
    consIggMgoMdoMt: toNumber(item.consIggMgoMdoMt),
    consIncineratorMgoMdoMt: toNumber(item.consIncineratorMgoMdoMt),
    consSystemOilL: toNumber(item.consSystemOilL),
    consCylinderOilL: toNumber(item.consCylinderOilL),
    consDgSystemOilL: toNumber(item.consDgSystemOilL),
    consHydrOilL: toNumber(item.consHydrOilL),
    consFwM3: toNumber(item.consFwM3),
    rhMeTotal: toNumber(item.rhMeTotal),
    rhMeDaily: toNumber(item.rhMeDaily),
    rhAux1Total: toNumber(item.rhAux1Total),
    rhAux1Daily: toNumber(item.rhAux1Daily),
    rhAux2Total: toNumber(item.rhAux2Total),
    rhAux2Daily: toNumber(item.rhAux2Daily),
    rhAux3Total: toNumber(item.rhAux3Total),
    rhAux3Daily: toNumber(item.rhAux3Daily),
    rhEmergencyAuxTotal: toNumber(item.rhEmergencyAuxTotal),
    rhEmergencyAuxDaily: toNumber(item.rhEmergencyAuxDaily),
    rhBoilerTotal: toNumber(item.rhBoilerTotal),
    rhBoilerDaily: toNumber(item.rhBoilerDaily),
    rhIggTotal: toNumber(item.rhIggTotal),
    rhIggDaily: toNumber(item.rhIggDaily),
    rhIncineratorTotal: toNumber(item.rhIncineratorTotal),
    rhIncineratorDaily: toNumber(item.rhIncineratorDaily),
    ...speedDisplays,
  };
}

export function mapFormDataToPayload(data: Record<string, unknown>): Record<string, unknown> {
  const {
    vessel,
    id,
    createdAt,
    updatedAt,
    createdByUserId,
    updatedByUserId,
    theoreticalSpeed,
    slipPercent,
    _theoreticalSpeedDisplay,
    _slipPercentDisplay,
    seaState,
    seaCurrentDirection,
    windFrom,
    swellDirection,
    ...rest
  } = data;

  const reportDate = rest.reportDate != null ? String(rest.reportDate).slice(0, 10) : undefined;
  const eta = rest.eta ? new Date(String(rest.eta)).toISOString() : undefined;
  const etd = rest.etd ? new Date(String(rest.etd)).toISOString() : undefined;

  return {
    ...rest,
    vesselId: toNumber(rest.vesselId),
    reportDate,
    reportTime: toInputString(rest.reportTime).trim() || undefined,
    gmtOffset: toInputString(rest.gmtOffset).trim() || undefined,
    condition: toInputString(rest.condition) || undefined,
    activities: Array.isArray(rest.activities) ? rest.activities : [],
    latitude: toInputString(rest.latitude).trim() || undefined,
    longitude: toInputString(rest.longitude).trim() || undefined,
    eta,
    etd,
    nextPort: toInputString(rest.nextPort).trim() || undefined,
    distanceCovered: toNumber(rest.distanceCovered),
    distanceToGo: toNumber(rest.distanceToGo),
    cargoType: toInputString(rest.cargoType).trim() || undefined,
    cargoOnBoardMt: toNumber(rest.cargoOnBoardMt),
    draftFwd: toNumber(rest.draftFwd),
    draftMid: toNumber(rest.draftMid),
    draftAft: toNumber(rest.draftAft),
    presentSpeed: toNumber(rest.presentSpeed),
    avgSpeed24h: toNumber(rest.avgSpeed24h),
    avgRpm: toNumber(rest.avgRpm),
    steamingHours: toNumber(rest.steamingHours),
    pitchMm: toNumber(rest.pitchMm),
    avgSog: toNumber(rest.avgSog),
    avgStw: toNumber(rest.avgStw),
    seaStateId: toOptionalId(rest.seaStateId),
    seaCurrentDirectionId: toOptionalId(rest.seaCurrentDirectionId),
    seaCurrentForce: toInputString(rest.seaCurrentForce).trim() || undefined,
    windFromId: toOptionalId(rest.windFromId),
    windForce: toInputString(rest.windForce).trim() || undefined,
    swellDirectionId: toOptionalId(rest.swellDirectionId),
    swellForce: toInputString(rest.swellForce).trim() || undefined,
    weatherNotes: toInputString(rest.weatherNotes).trim() || undefined,
    notes: toInputString(rest.notes).trim() || undefined,
    robFoMt: toNumber(rest.robFoMt),
    robMgoMdoMt: toNumber(rest.robMgoMdoMt),
    robSystemOilL: toNumber(rest.robSystemOilL),
    robCylinderOilL: toNumber(rest.robCylinderOilL),
    robDgSystemOilL: toNumber(rest.robDgSystemOilL),
    robHydrOilL: toNumber(rest.robHydrOilL),
    robFwM3: toNumber(rest.robFwM3),
    robSludgesM3: toNumber(rest.robSludgesM3),
    robBilgesM3: toNumber(rest.robBilgesM3),
    consMeFoMt: toNumber(rest.consMeFoMt),
    consDgFoMt: toNumber(rest.consDgFoMt),
    consBlrFoMt: toNumber(rest.consBlrFoMt),
    consIggFoMt: toNumber(rest.consIggFoMt),
    consMeMgoMdoMt: toNumber(rest.consMeMgoMdoMt),
    consDgMgoMdoMt: toNumber(rest.consDgMgoMdoMt),
    consBlrMgoMdoMt: toNumber(rest.consBlrMgoMdoMt),
    consIggMgoMdoMt: toNumber(rest.consIggMgoMdoMt),
    consIncineratorMgoMdoMt: toNumber(rest.consIncineratorMgoMdoMt),
    consSystemOilL: toNumber(rest.consSystemOilL),
    consCylinderOilL: toNumber(rest.consCylinderOilL),
    consDgSystemOilL: toNumber(rest.consDgSystemOilL),
    consHydrOilL: toNumber(rest.consHydrOilL),
    consFwM3: toNumber(rest.consFwM3),
    rhMeTotal: toNumber(rest.rhMeTotal),
    rhMeDaily: toNumber(rest.rhMeDaily),
    rhAux1Total: toNumber(rest.rhAux1Total),
    rhAux1Daily: toNumber(rest.rhAux1Daily),
    rhAux2Total: toNumber(rest.rhAux2Total),
    rhAux2Daily: toNumber(rest.rhAux2Daily),
    rhAux3Total: toNumber(rest.rhAux3Total),
    rhAux3Daily: toNumber(rest.rhAux3Daily),
    rhEmergencyAuxTotal: toNumber(rest.rhEmergencyAuxTotal),
    rhEmergencyAuxDaily: toNumber(rest.rhEmergencyAuxDaily),
    rhBoilerTotal: toNumber(rest.rhBoilerTotal),
    rhBoilerDaily: toNumber(rest.rhBoilerDaily),
    rhIggTotal: toNumber(rest.rhIggTotal),
    rhIggDaily: toNumber(rest.rhIggDaily),
    rhIncineratorTotal: toNumber(rest.rhIncineratorTotal),
    rhIncineratorDaily: toNumber(rest.rhIncineratorDaily),
  };
}

export const recalcSpeedDisplayFields: NonNullable<
  import('../../../components/generic/fieldRenderers').FieldConfig['onChangeSideEffect']
> = (_value, _setFieldValue, setMultipleFieldValues, getFormData) => {
  const fd = getFormData?.() ?? {};
  setMultipleFieldValues(speedDisplayValues(fd.pitchMm, fd.avgRpm, fd.avgStw));
};
